import { areDifferent } from 'utils/array';

export const RiskMatrixMinSize: number = 2;
export const RiskMatrixMaxSize: number = 7;
export const RiskMatrixDefSize: number = 3;

export const defClassificationColor = '#aaaaaa';

//
// RiskMatrix
//
export default class RiskMatrix {
  sizeLikelihood: number;

  sizeImpact: number;

  private _size3d: number;

  label3d: string;

  enable3d: boolean;

  matrix: number[][][];

  explainationLinkId: number;

  classifications: RiskMatrixClassification[];

  //
  // Make sure that size3d is always 1 when enable3d is false
  //
  get size3d() {
    return this.enable3d ? this._size3d : 1;
  }

  set size3d(value: number) {
    this._size3d = this.enable3d ? value : 1;
  }

  constructor() {
    this.sizeLikelihood = RiskMatrixDefSize;
    this.sizeImpact = RiskMatrixDefSize;
    this._size3d = RiskMatrixDefSize;
    this.label3d = '';
    this.enable3d = false;
    this.matrix = [];
    this.explainationLinkId = -1;
    this.classifications = [];
  }

  toJSON(): string {
    const model = {
      matrix: this.matrix,
      sizeLikelihood: this.sizeLikelihood,
      sizeImpact: this.sizeImpact,
      size3d: this._size3d,
      label3d: this.label3d,
      enable3d: this.enable3d,
      explainationLinkId: this.explainationLinkId,
      classifications: this.classifications.map((r: RiskMatrixClassification) => {
        return {
          id: r.id,
          name: r.name,
          color: r.color,
          timeFrame: r.timeFrame,
          cost: r.cost,
        };
      }),
    };

    return JSON.stringify(model);
  }

  fromJSON(patternString: string | undefined) {
    try {
      if (patternString) {
        const patternObject = JSON.parse(patternString);

        if (!patternObject.sizeLikelihood) {
          // Old format: convert to new format
          this.sizeLikelihood = patternObject.matrix;
          this.sizeImpact = patternObject.matrix;
          this._size3d = 1; // Default to 1 layer
          this.label3d = '';
          this.enable3d = false;
          this.explainationLinkId = patternObject.explainationLinkId;

          // Initialize matrix with zeros
          this.matrix = Array.from({ length: this._size3d }, () =>
            Array.from({ length: this.sizeImpact }, () => Array(this.sizeLikelihood).fill(0)),
          );

          // Create default classifications based on priorities
          for (const priority of patternObject.priorities) {
            const newClassification = new RiskMatrixClassification();

            newClassification.id = this.classifications.length + 1;
            newClassification.name = priority.name;
            newClassification.color = priority.color;
            newClassification.timeFrame = priority.timeFrame;
            newClassification.cost = priority.cost;

            this.classifications.push(newClassification);
            this.setClassification(priority.start, priority.end, newClassification.id);
          }
        } else {
          this.matrix = patternObject.matrix as number[][][]; // Assume 3D
          this.sizeLikelihood = patternObject.sizeLikelihood as number;
          this.sizeImpact = patternObject.sizeImpact as number;
          this._size3d = patternObject.size3d || 1; // Default to 1 if not present
          this.label3d = patternObject.label3d || '';
          this.enable3d = patternObject.enable3d || false;
          this.explainationLinkId = patternObject.explainationLinkId as number;

          for (const item of patternObject.classifications) {
            const classification = item as RiskMatrixClassification;
            const newClassification = new RiskMatrixClassification();
            newClassification.id = classification.id;
            newClassification.name = classification.name;
            newClassification.color = classification.color;
            newClassification.timeFrame = classification.timeFrame;
            newClassification.cost = classification.cost;
            this.classifications.push(newClassification);
          }

          this.upgradeMatrixTo3D();
        }
      }
    } catch {
      // Invalid input. Ignore and rely on the default values set in the constructor
    }
  }

  private upgradeMatrixTo3D() {
    // Check if the matrix is 2D
    if (Array.isArray(this.matrix) && Array.isArray(this.matrix[0]) && !Array.isArray(this.matrix[0][0])) {
      const oldMatrix = this.matrix as unknown as number[][];
      // Create a new 3D matrix with one layer and copy the existing data
      this._size3d = 1; // first set to 1 to let resize notice the change
      this.matrix = Array.from({ length: this._size3d }, () => {
        return oldMatrix.map((row) => row.map((cell) => cell));
      });
      if (this.enable3d) {
        this.resizeMatrixDim3(RiskMatrixDefSize); // Ensure proper sizing
      }
    }
  }

  private fillNewLayerWithExistingClassification(layerIndex: number): number[][] {
    //create a new layer and fill each cell with the classification Id calculated by likelhood * impact * (layerIndex + 1)
    const newLayer: number[][] = Array.from({ length: this.sizeImpact }, () => Array(this.sizeLikelihood).fill(0));

    for (let i = 0; i < this.sizeImpact; i++) {
      for (let j = 0; j < this.sizeLikelihood; j++) {
        const classification = this.getClassificationForScore((layerIndex + 1) * (i + 1) * (j + 1));
        if (classification) {
          newLayer[i][j] = classification.id;
        } else {
          if (this.classifications.length > 0) {
            newLayer[i][j] = this.classifications[this.classifications.length - 1].id; // Default to highest classification
          } else {
            newLayer[i][j] = 0; // No classifications defined
          }
        }
      }
    }

    return newLayer;
  }

  getColor(likelihood: number, impact: number, dim3: number): string {
    const classification = this.getClassification(likelihood, impact, dim3);

    if (!classification) {
      return defClassificationColor;
    }

    return classification.color;
  }

  getClassification(likelihood: number, impact: number, dim3: number): RiskMatrixClassification | undefined {
    try {
      const valDim3 = this.enable3d ? dim3 : 1;
      const cell = this.matrix[valDim3 - 1][impact - 1][likelihood - 1];

      return this.classifications.find((c) => c.id === cell);
    } catch {
      return undefined;
    }
  }

  private getClassificationForScore(score: number): RiskMatrixClassification | undefined {
    // Find the first classification in the matrix that has this score
    // - If the score is higher than the highest score in the matrix that has a classification, return the highest classification
    // - If the score is lower than the lowest score in the matrix that has a classification, return the lowest classification
    let highestClassification: RiskMatrixClassification | undefined = undefined;
    let lowestClassification: RiskMatrixClassification | undefined = undefined;
    let highestScoreWithClassification = 0;
    let lowestScoreWithClassification = RiskMatrixMaxSize ** 3 + 1;

    for (let k = 1; k <= this._size3d; k++) {
      for (let i = 1; i <= this.sizeImpact; i++) {
        for (let j = 1; j <= this.sizeLikelihood; j++) {
          const cellClassificationId = this.matrix[k - 1][i - 1][j - 1];
          if (cellClassificationId) {
            const cellScore = k * i * j;
            const classification = this.classifications.find((c) => c.id === cellClassificationId);
            if (classification) {
              if (cellScore === score) {
                return classification;
              }
              if (cellScore > highestScoreWithClassification) {
                highestScoreWithClassification = cellScore;
                highestClassification = classification;
              }
              if (cellScore < lowestScoreWithClassification) {
                lowestScoreWithClassification = cellScore;
                lowestClassification = classification;
              }
            }
          }
        }
      }
    }

    if (score > highestScoreWithClassification) {
      return highestClassification;
    }
    if (score < lowestScoreWithClassification) {
      return lowestClassification;
    }

    // there is no classification at all
    return undefined;
  }

  setClassificationCell(classificationId: number, likelihood: number, impact: number, dim3: number) {
    try {
      this.matrix[dim3 - 1][impact - 1][likelihood - 1] = classificationId;
    } catch {
      // Ignore out of bound errors
    }
  }

  setClassification(lower: number, upper: number, classificationId: number, layerMatrix?: number[][][]) {
    const currentIdx = this.classifications.findIndex((c) => c.id === classificationId);
    if (currentIdx < 0) {
      return;
    }

    // Determine the previous and next classifications to fill the matrix when the range is reduced
    const prevClassification = currentIdx > 0 ? this.classifications[currentIdx - 1] : undefined;
    const nextClassification =
      currentIdx < this.classifications.length ? this.classifications[currentIdx + 1] : undefined;

    for (let k = 1; k <= this._size3d; k++) {
      for (let i = 1; i <= this.sizeImpact; i++) {
        for (let j = 1; j <= this.sizeLikelihood; j++) {
          const score = k * i * j;
          if (score >= lower && score <= upper) {
            // Fill matrix with the new classification id for the range
            this.matrix[k - 1][i - 1][j - 1] = classificationId;
          } else {
            if (layerMatrix) {
              // Copy the value from the layer matrix
              this.matrix[k - 1][i - 1][j - 1] = layerMatrix[k - 1][i - 1][j - 1];
            }
            if (this.matrix[k - 1][i - 1][j - 1] === classificationId) {
              // If the cell is outside the range and still the classification id (after copy from layer)
              // we need to fill it with the previous or next classification id, if available
              if (prevClassification && score < lower) {
                this.matrix[k - 1][i - 1][j - 1] = prevClassification.id;
              } else if (nextClassification && score > upper) {
                this.matrix[k - 1][i - 1][j - 1] = nextClassification.id;
              }
            }
          }
        }
      }
    }
  }

  getClassificationLowestScore(classificationId: number | undefined): number {
    const classification = this.classifications.find((c) => c.id === classificationId);
    if (!classification) {
      return 1;
    }

    let lowest: number = RiskMatrixMaxSize ** 3 + 1;

    for (let k = 1; k <= this.size3d; k++) {  //use size3d instead of _size3d
      for (let i = 1; i <= this.sizeImpact; i++) {
        for (let j = 1; j <= this.sizeLikelihood; j++) {
          if (this.matrix[k - 1][i - 1][j - 1] === classificationId) {
            const score = k * i * j;
            if (score < lowest) {
              lowest = score;
            }
          }
        }
      }
    }

    return lowest;
  }

  getClassificationHighestScore(classificationId: number | undefined): number {
    const classification = this.classifications.find((c) => c.id === classificationId);
    if (!classification) {
      return 1;
    }

    let highest: number = 0;

    for (let k = this.size3d; k >= 1; k--) {  //use size3d instead of _size3d
      for (let i = this.sizeImpact; i >= 1; i--) {
        for (let j = this.sizeLikelihood; j >= 1; j--) {
          if (this.matrix[k - 1][i - 1][j - 1] === classificationId) {
            const score = k * i * j;
            if (score > highest) {
              highest = score;
            }
          }
        }
      }
    }

    return highest;
  }

  getNewClassification(): RiskMatrixClassification {
    const newClassification = new RiskMatrixClassification();
    newClassification.id = this.classifications.length + 1;

    return newClassification;
  }

  resizeMatrixLikelihood(size: number) {
    if (size < RiskMatrixMinSize || size > RiskMatrixMaxSize) {
      return;
    }

    this.normalizeClassifications();
    this.resizeMatrix(this.sizeLikelihood, this.sizeImpact, this._size3d, size, this.sizeImpact, this._size3d);

    this.sizeLikelihood = size;
  }

  resizeMatrixImpact(size: number) {
    if (size < RiskMatrixMinSize || size > RiskMatrixMaxSize) {
      return;
    }

    this.normalizeClassifications();
    this.resizeMatrix(this.sizeLikelihood, this.sizeImpact, this._size3d, this.sizeLikelihood, size, this._size3d);

    this.sizeImpact = size;
  }

  resizeMatrixDim3(size: number) {
    if (size < RiskMatrixMinSize || size > RiskMatrixMaxSize) {
      return;
    }

    this.normalizeClassifications();
    this.resizeMatrix(this.sizeLikelihood, this.sizeImpact, this._size3d, this.sizeLikelihood, this.sizeImpact, size);

    this._size3d = size;
  }

  resizeMatrix(
    oldLikelihood: number,
    oldImpact: number,
    oldDim3: number,
    newLikelihood: number,
    newImpact: number,
    newDim3: number,
  ) {
    const no2DChange = oldLikelihood === newLikelihood && oldImpact === newImpact;
    const no3DChange = oldDim3 === newDim3;

    if (no2DChange && no3DChange) {
      return;
    }

    // If there are no classifications, nothing to do
    if (this.classifications.length === 0) return;

    // Create new matrix with resized dimensions
    const oldMatrix = this.matrix as number[][][];
    const newMatrix: number[][][] = Array.from({ length: newDim3 }, () => []);
    const layersToCopy = Math.min(oldDim3, newDim3);
    const need2DResize = !no2DChange;

    // Resize or copy existing layers
    for (let k = 0; k < layersToCopy; k++) {
      const oldLayer = oldMatrix[k] || [];

      if (need2DResize) {
        newMatrix[k] = this.resize2DLayer(oldLayer, oldLikelihood, oldImpact, newLikelihood, newImpact);
      } else {
        // 2D dimensions same, just deep copy the layer
        newMatrix[k] = oldLayer.map((row) => row.slice());
      }
    }

    // Add new layers (if any), filled with highest classification
    for (let layer = layersToCopy; layer < newDim3; layer++) {
      newMatrix[layer] = this.fillNewLayerWithExistingClassification(layer);
    }

    // If newDim3 < oldDim3, extra old layers are implicitly dropped by not copying

    this.matrix = newMatrix;
  }

  // Helper that resizes a single 2D layer using your existing logic
  private resize2DLayer(
    layer: number[][],
    oldLikelihood: number,
    oldImpact: number,
    newLikelihood: number,
    newImpact: number,
  ): number[][] {
    // No change in 2D dimensions -> just deep copy
    if (oldLikelihood === newLikelihood && oldImpact === newImpact) {
      return layer.map((row) => row.slice());
    }

    // Create empty layer
    const emptyLayer: number[][] = Array.from({ length: newImpact }, () => Array(newLikelihood).fill(0));

    // Determine if we have a significant downscale
    const isSignificantDownscale = oldLikelihood > 2 * newLikelihood || oldImpact > 2 * newImpact;

    if (isSignificantDownscale) {
      // --- Drastic downscale branch ---

      const uniqueClassifications = new Set<number>();
      for (let i = 0; i < oldImpact; i++) {
        for (let j = 0; j < oldLikelihood; j++) {
          uniqueClassifications.add(layer[i][j]);
        }
      }

      const scoreToClassification = new Map<number, number>();
      for (let i = 0; i < oldImpact; i++) {
        for (let j = 0; j < oldLikelihood; j++) {
          const score = (i + 1) * (j + 1); // 1-based scores
          scoreToClassification.set(score, layer[i][j]);
        }
      }

      const classificationToMinScore = new Map<number, number>();
      uniqueClassifications.forEach((classId) => {
        let minScore = Number.MAX_SAFE_INTEGER;
        scoreToClassification.forEach((id, score) => {
          if (id === classId && score < minScore) {
            minScore = score;
          }
        });
        classificationToMinScore.set(classId, minScore);
      });

      const sortedClassifications = Array.from(uniqueClassifications).sort(
        (a, b) => (classificationToMinScore.get(a) || 0) - (classificationToMinScore.get(b) || 0),
      );

      for (let i = 0; i < newImpact; i++) {
        for (let j = 0; j < newLikelihood; j++) {
          const normalizedPos = ((i + 1) * (j + 1)) / (newImpact * newLikelihood);

          const classIndex = Math.min(
            Math.floor(normalizedPos * sortedClassifications.length),
            sortedClassifications.length - 1,
          );

          emptyLayer[i][j] = sortedClassifications[classIndex];
        }
      }
    } else {
      // --- Normal scaling branch ---

      const getClassificationHistogram = (
        startI: number,
        endI: number,
        startJ: number,
        endJ: number,
      ): Map<number, number> => {
        const histogram = new Map<number, number>();

        for (let i = startI; i < endI; i++) {
          for (let j = startJ; j < endJ; j++) {
            if (i >= 0 && i < oldImpact && j >= 0 && j < oldLikelihood) {
              const value = layer[i][j];
              histogram.set(value, (histogram.get(value) || 0) + 1);
            }
          }
        }

        return histogram;
      };

      const scaleI = oldImpact / newImpact;
      const scaleJ = oldLikelihood / newLikelihood;

      for (let i = 0; i < newImpact; i++) {
        for (let j = 0; j < newLikelihood; j++) {
          const startI = Math.floor(i * scaleI);
          const endI = Math.ceil((i + 1) * scaleI);
          const startJ = Math.floor(j * scaleJ);
          const endJ = Math.ceil((j + 1) * scaleJ);

          const histogram = getClassificationHistogram(startI, endI, startJ, endJ);

          let mostFrequentID = 0;
          let maxCount = 0;

          histogram.forEach((count, id) => {
            if (count > maxCount || (count === maxCount && id > mostFrequentID)) {
              maxCount = count;
              mostFrequentID = id;
            }
          });

          emptyLayer[i][j] = mostFrequentID;
        }
      }
    }

    return emptyLayer;
  }

  normalizeClassifications() {
    //
    // Normalize the classification ids
    // This is needed when classifications are removed or added to ensure that the ids are sequential and start from 1
    // This ensures we can expand the matrix correctly
    // 1. Replace the classification ids in the matrix with a new id starting from 1000
    //    (this is to avoid conflicts with existing ids in the matrix)
    // 2. Divide the ids by 1000 to get the new ids
    // 3. Set the new ids in the classifications array
    //
    if (this.classifications.length === 0) {
      return;
    }

    // When the classifications are already starting at Id 1 and are linear, we don't need to normalize
    let isLinear = true;
    for (let i = 0; i < this.classifications.length; i++) {
      if (this.classifications[i].id !== i + 1) {
        isLinear = false;
        break;
      }
    }
    if (isLinear) {
      return;
    }

    let newId = 1;
    const factor = 1024; // Use a factor to avoid conflicts with existing ids

    for (let idx = 0; idx < this.classifications.length; idx++) {
      const id = this.classifications[idx].id;
      this.replaceClassificationId(id, newId * factor);
      newId++;
    }

    // Now divide the ids in the matrix by 1000 to get the new ids
    for (let i = 0; i < this.matrix.length; i++) {
      for (let j = 0; j < this.matrix[i].length; j++) {
        for (let k = 0; k < this.matrix[i][j].length; k++) {
          this.matrix[i][j][k] = this.matrix[i][j][k] / factor;
        }
      }
    }

    // Now we have the ids in the matrix, we can set the new ids in the classifications
    for (let idx = 1; idx <= this.classifications.length; idx++) {
      this.classifications[idx - 1].id = idx;
    }
  }

  replaceClassificationId(oldId: number, newId: number) {
    // Replace the classification id in the matrix and in the classifications array
    if (oldId === newId) {
      return;
    }

    for (let i = 0; i < this.matrix.length; i++) {
      for (let j = 0; j < this.matrix[i].length; j++) {
        for (let k = 0; k < this.matrix[i][j].length; k++) {
          if (this.matrix[i][j][k] === oldId) {
            this.matrix[i][j][k] = newId;
          }
        }
      }
    }
  }

  getDefaultCellSize = (): number => {
    const maxSize = Math.max(this.sizeImpact, this.sizeLikelihood);

    return Math.min(300 / maxSize, 90);
  };

  isEqual(orgModel: RiskMatrix): boolean {
    if (!orgModel || !this.classifications || !orgModel.classifications) {
      return false;
    }
    if (orgModel.classifications.length !== this.classifications.length) {
      return false;
    }
    if (orgModel.sizeLikelihood !== this.sizeLikelihood || orgModel.sizeImpact !== this.sizeImpact) {
      return false;
    }
    if (orgModel._size3d !== this._size3d || orgModel.label3d !== this.label3d || orgModel.enable3d !== this.enable3d) {
      return false;
    }
    if (orgModel.explainationLinkId !== this.explainationLinkId) {
      return false;
    }

    if (
      areDifferent<RiskMatrixClassification>(orgModel.classifications, this.classifications, (a, b) => a.isEqual(b))
    ) {
      return false;
    }

    for (let i = 0; i < this.matrix.length; i++) {
      if (this.matrix[i].length !== orgModel.matrix[i].length) {
        return false;
      }
      for (let j = 0; j < this.matrix[i].length; j++) {
        if (this.matrix[i][j].length !== orgModel.matrix[i][j].length) {
          return false;
        }
        for (let k = 0; k < this.matrix[i][j].length; k++) {
          if (this.matrix[i][j][k] !== orgModel.matrix[i][j][k]) {
            return false;
          }
        }
      }
    }

    return true;
  }

  clone(): RiskMatrix {
    const newRiskMatrix = new RiskMatrix();
    newRiskMatrix.sizeLikelihood = this.sizeLikelihood;
    newRiskMatrix.sizeImpact = this.sizeImpact;
    newRiskMatrix._size3d = this._size3d;
    newRiskMatrix.label3d = this.label3d;
    newRiskMatrix.enable3d = this.enable3d;
    newRiskMatrix.matrix = this.matrix.map((row) => row.map((cell) => [...cell]));
    newRiskMatrix.explainationLinkId = this.explainationLinkId;
    newRiskMatrix.classifications = this.classifications.map((c) => c.clone());

    return newRiskMatrix;
  }
}

//
// RiskMatrix Classification
//
export class RiskMatrixClassification {
  id: number;

  name: string;

  color: string;

  timeFrame: string;

  cost: string;

  constructor() {
    this.id = -1;
    this.name = '';
    this.color = '#aaaaaa';
    this.timeFrame = '';
    this.cost = '';
  }

  clone(): RiskMatrixClassification {
    const newClassification = new RiskMatrixClassification();
    newClassification.id = this.id;
    newClassification.name = this.name;
    newClassification.color = this.color;
    newClassification.timeFrame = this.timeFrame;
    newClassification.cost = this.cost;

    return newClassification;
  }

  isEqual(other: RiskMatrixClassification): boolean {
    return (
      this.id === other.id &&
      this.name === other.name &&
      this.color === other.color &&
      this.timeFrame === other.timeFrame &&
      this.cost === other.cost
    );
  }
}
